import { useEffect, useMemo, useRef, useState } from "react";
import PageShell from "@/components/PageShell";
import api from "@/lib/api";
import { Landmark, Gavel, Search, FileText, Download, Loader2, BookOpenText, X, Sparkles, CloudDownload, Check } from "lucide-react";
import { saveItem, getItem, listKeys } from "@/lib/offline";
import { ReadAloud } from "@/components/ReadAloud";
import { toast } from "sonner";

export default function States() {
  const [states, setStates] = useState([]);
  const [query, setQuery] = useState("");
  const [downloading, setDownloading] = useState("");
  const [status, setStatus] = useState({});      // slug -> {status, chars, partial}
  const [reader, setReader] = useState(null);     // {name, text, partial, loading}
  const [savedC, setSavedC] = useState(new Set());
  const [savingC, setSavingC] = useState("");
  const polls = useRef({});

  useEffect(() => {
    api.get("/states").then((r) => setStates(r.data));
    api.get("/states/constitution/status").then((r) => setStatus(r.data)).catch(() => {});
    listKeys().then((keys) => setSavedC(new Set(keys.filter((k) => k.startsWith("constitution:")).map((k) => k.slice(13)))));
  }, []);

  const saveOffline = async (s) => {
    setSavingC(s.slug);
    try {
      const r = await api.get(`/states/${s.slug}/constitution?full=1`);
      await saveItem(`constitution:${s.slug}`, { name: s.name, text: r.data.text || "", partial: r.data.partial });
      setSavedC((m) => new Set(m).add(s.slug));
      toast.success(`${s.name} saved for offline`);
    } catch { toast.error("Could not save offline"); }
    finally { setSavingC(""); }
  };

  const poll = (slug) => {
    if (polls.current[slug]) return;
    polls.current[slug] = setInterval(async () => {
      try {
        const r = await api.get(`/states/${slug}/constitution`);
        setStatus((m) => ({ ...m, [slug]: r.data }));
        if (["ready", "partial", "error"].includes(r.data.status)) {
          clearInterval(polls.current[slug]); delete polls.current[slug];
        }
      } catch { /* keep polling */ }
    }, 5000);
  };
  useEffect(() => () => Object.values(polls.current).forEach(clearInterval), []);

  const prepare = async (s) => {
    setStatus((m) => ({ ...m, [s.slug]: { status: "building" } }));
    try {
      const r = await api.post(`/states/${s.slug}/constitution/build`);
      setStatus((m) => ({ ...m, [s.slug]: { ...(m[s.slug] || {}), status: r.data.status } }));
      if (r.data.status === "building") poll(s.slug);
    } catch { toast.error("Could not start. Try again."); setStatus((m) => ({ ...m, [s.slug]: { status: "error" } })); }
  };

  const openReader = async (s) => {
    setReader({ name: s.name, loading: true });
    try {
      const r = await api.get(`/states/${s.slug}/constitution?full=1`);
      setReader({ name: s.name, text: r.data.text || "", partial: r.data.partial, loading: false });
    } catch {
      const cached = await getItem(`constitution:${s.slug}`);
      if (cached) setReader({ name: s.name, text: cached.text, partial: cached.partial, loading: false, offline: true });
      else { toast.error("Could not load text"); setReader(null); }
    }
  };

  const downloadFile = async (s, kind) => {
    setDownloading(`${s.slug}-${kind}`);
    try {
      const path = kind === "full" ? `/states/${s.slug}/constitution/pdf` : `/states/${s.slug}/pdf`;
      const res = await api.get(path, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = kind === "full" ? `${s.slug}-constitution-full.pdf` : `${s.slug}-constitution-reference.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Could not generate the PDF."); }
    finally { setDownloading(""); }
  };

  const filtered = useMemo(
    () => states.filter((s) => s.name.toLowerCase().includes(query.toLowerCase())),
    [states, query]
  );

  return (
    <PageShell>
      <div className="max-w-6xl mx-auto px-6 py-14">
        <div className="flex items-center gap-3 mb-2">
          <Landmark className="w-7 h-7 text-primary" />
          <h1 className="font-serif text-5xl">State Law Library</h1>
        </div>
        <p className="text-muted-foreground max-w-2xl mb-8">
          Every U.S. state's constitution and its official court rules. Prepare the <strong>complete constitution
          text</strong> for in-app reading and offline PDF download — fetched from public sources.
        </p>

        <div className="relative max-w-md mb-10">
          <Search className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="states-search" value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Find a state…"
            className="w-full bg-muted border border-input pl-10 pr-3 py-3 rounded-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors" />
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-border border border-border">
          {filtered.map((s) => {
            const st = status[s.slug];
            const built = st && (st.status === "ready" || st.status === "partial");
            const building = st && st.status === "building";
            return (
              <div key={s.slug} className="bg-card p-6 flex flex-col" data-testid={`state-${s.slug}`}>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-serif text-2xl">{s.name}</h2>
                  {st?.partial && <span className="text-[9px] uppercase tracking-widest font-mono text-amber-400/90 border border-amber-400/40 px-1.5 py-0.5">partial</span>}
                </div>
                <div className="flex flex-col gap-2 mb-4">
                  <a href={s.constitution_url} target="_blank" rel="noreferrer" data-testid={`state-constitution-${s.slug}`}
                    className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors">
                    <FileText className="w-4 h-4 text-primary" /> Source (opens Justia)
                  </a>
                  <a href={s.courts_url} target="_blank" rel="noreferrer" data-testid={`state-courts-${s.slug}`}
                    className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors">
                    <Gavel className="w-4 h-4 text-primary" /> Court rules &amp; procedure
                  </a>
                </div>

                <div className="mt-auto pt-3 border-t border-border">
                  {!built && !building && (
                    <button onClick={() => prepare(s)} data-testid={`state-prepare-${s.slug}`}
                      className="w-full flex items-center justify-center gap-2 text-sm bg-primary/10 border border-primary/30 text-primary px-3 py-2 rounded-sm hover:bg-primary/20 transition-colors">
                      <Sparkles className="w-4 h-4" /> Prepare full text
                    </button>
                  )}
                  {building && (
                    <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground py-2" data-testid={`state-building-${s.slug}`}>
                      <Loader2 className="w-4 h-4 animate-spin text-primary" /> Fetching… (~30s)
                    </div>
                  )}
                  {st?.status === "error" && !building && (
                    <button onClick={() => prepare(s)} className="w-full text-sm text-amber-400/90 py-2">Fetch failed — retry</button>
                  )}
                  {built && (
                    <div className="flex flex-col gap-2">
                      <button onClick={() => openReader(s)} data-testid={`state-read-${s.slug}`}
                        className="flex items-center gap-2 text-sm text-foreground hover:text-primary transition-colors">
                        <BookOpenText className="w-4 h-4 text-primary" /> Read full constitution
                      </button>
                      <button onClick={() => downloadFile(s, "full")} disabled={downloading === `${s.slug}-full`} data-testid={`state-fullpdf-${s.slug}`}
                        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors disabled:opacity-60">
                        {downloading === `${s.slug}-full` ? <Loader2 className="w-4 h-4 animate-spin text-primary" /> : <Download className="w-4 h-4 text-primary" />} Download full PDF
                      </button>
                      <button onClick={() => saveOffline(s)} disabled={savingC === s.slug} data-testid={`state-offline-${s.slug}`}
                        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors disabled:opacity-60">
                        {savingC === s.slug ? <Loader2 className="w-4 h-4 animate-spin text-primary" /> : savedC.has(s.slug) ? <Check className="w-4 h-4 text-primary" /> : <CloudDownload className="w-4 h-4 text-primary" />} {savedC.has(s.slug) ? "Saved offline" : "Save offline"}
                      </button>
                    </div>
                  )}
                  <button onClick={() => downloadFile(s, "ref")} disabled={downloading === `${s.slug}-ref`} data-testid={`state-pdf-${s.slug}`}
                    className="mt-2 flex items-center gap-2 text-xs text-muted-foreground/80 hover:text-primary transition-colors disabled:opacity-60">
                    {downloading === `${s.slug}-ref` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Quick reference sheet
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        {filtered.length === 0 && <p className="text-muted-foreground mt-6">No state matches your search.</p>}
      </div>

      {reader && (
        <div className="fixed inset-0 z-[90] bg-black/70 flex items-center justify-center p-4" onClick={() => setReader(null)} data-testid="constitution-reader">
          <div className="bg-card border border-border rounded-lg max-w-3xl w-full max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <div>
                <h3 className="font-serif text-2xl">{reader.name} Constitution</h3>
                {reader.partial && <span className="text-xs text-amber-400/90 font-mono">Partial capture — some sections may be missing</span>}
              </div>
              <div className="flex items-center gap-4">
                {reader.text && <ReadAloud label="Read aloud" getText={() => reader.text} />}
                <button onClick={() => setReader(null)} className="text-muted-foreground hover:text-foreground"><X className="w-5 h-5" /></button>
              </div>
            </div>
            <div className="px-6 py-5 overflow-y-auto">
              {reader.loading ? (
                <div className="flex items-center justify-center py-16 text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin" /></div>
              ) : (
                <pre className="whitespace-pre-wrap font-serif text-[15px] leading-relaxed text-foreground">{reader.text}</pre>
              )}
            </div>
            <div className="px-6 py-3 border-t border-border text-xs text-muted-foreground font-mono">
              For educational purposes only. None of this shall be construed as legal advice.
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
}
